<template>
  <div class="selection">
    <header>
      <div class="selection-details">
        <h2 @click="toggleMinified">
          <span class="selection-number">{{ index }}.</span>
          {{ selection.title }}
          <template v-if="minified && noItems">
            / {{ noItems }}
          </template>
          <template v-if="minified && frequency">
            every {{ frequency }}
          </template>
          <template v-if="minified && selectionPrice">
            / A$ {{ selectionPrice }}
          </template>
        </h2>

        <transition name="slide-fade">
          <template v-if="!minified">
            <div class="flex-container">
              <div class="description-container">
                <p v-html="selection.abstract" />

                <template v-if="selection.body">
                  <a
                    class="btn btn-outline-primary btn-small"
                    href="javascript:"
                    @click="toggleMore('body')"
                  >
                    Read
                    <template v-if="showMore.body">Less
                    </template>
                    <template v-else>More</template>
                  </a>

                  <transition name="slide-fade">
                    <template v-if="showMore.body">
                      <p
                        class="more-text"
                        v-html="selection.body"
                      />
                    </template>
                  </transition>
                </template>

                <section class="cta-container">
                  <div
                    v-if="selectionPrice"
                    class="selection-price"
                  >
                    <template v-if="!customClub">
                      <template v-if="selectionRRP">
                        <div class="price-rrp strike-through">
                          <span class="currency">A$</span>
                          <span class="amount">{{ selectionRRP }}</span>
                          <span title="Recommended Retail Price">RRP</span>
                        </div>
                      </template>
                      <span class="currency">A$</span>
                      <span class="amount">{{ selectionPrice }}</span>
                      <div class="frequency">
                        Get <strong>{{ noItems }}</strong>
                        every <strong>{{ frequency }}</strong>
                      </div>
                    </template>
                    <template v-else>
                      Choose <strong>{{ noItems }}</strong> from our finest wines and
                      get them delivered every <strong>{{ frequency }}</strong> to your home
                      and save.
                    </template>
                  </div>
                  <div class="join-club">
                    <router-link
                      :to="`/wine-club/preselect/${selection.id}`"
                      class="btn btn-cta btn-primary btn-large"
                    >
                      Join This Club
                    </router-link>
                  </div>
                </section>
              </div>

              <div class="side-container">
                <div
                  v-if="discount"
                  class="discount"
                >
                  <div class="label">
                    Discount
                  </div>
                  <div class="amount">
                    <template v-if="discountIsPercent">
                      {{ discount }} %
                    </template>
                    <template v-else>
                      <div class="price">
                        <span class="currency">A$</span>
                        <span class="amount">{{ discount }}</span>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="shipping-schedule-headline">
                  <h4>Shipping Schedule</h4>
                </div>
                <div class="selection-dates-timeline">
                  <ul>
                    <li :class="{ passed: checkDatePassed(selection.lastshippeddate) }">
                      <span class="date">{{ lastShippedDate }}</span>
                      <span class="label">Previous Shipping</span>
                    </li>
                    <li :class="{ passed: checkDatePassed(selection.lockdate) }">
                      <span class="date">{{ lockedByDate }}</span>
                      <span class="label">Cut off Date</span>
                    </li>
                    <li :class="{ passed: checkDatePassed(selection.processingdate) }">
                      <span class="date">{{ processingDate }}</span>
                      <span class="label">Orders being processed</span>
                    </li>
                    <li :class="{ passed: checkDatePassed(selection.nextshippeddate) }">
                      <span class="date">{{ nextShippedDate }}</span>
                      <span class="label">Upcoming Shipping</span>
                    </li>
                  </ul>
                </div>

                <div class="selection-dates">
                  <div class="date-container">
                    <span class="label">Frequency:</span> <span class="date">{{ frequency }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </transition>
      </div>
    </header>
    <div
      v-if="!minified && !customClub"
      class="products-wrapper"
    >
      <h2 class="products-headline">
        Products in this Selection
      </h2>
      <div class="products-container">
        <Product
          v-for="(item, index) in combinedProducts"
          :key="index"
          :product="item"
          :custom-club="customClub"
        />
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import Product from './Product'
import { roleAdjustPrice } from '@aspedia/vuejs-ecommerce-ui/features/role_price_adjustments'

export default {
  name: 'selections',
  components: {
    Product
  },
  props: {
    selection: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    customClub: {
      type: Boolean,
      required: true
    },
    isMinified: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showMore: {
        body: false
      },
      minified: this.isMinified
    }
  },
  computed: {
    combinedProducts() {
      return this.selection.products.reduce((acc, product) => {
        const id = product.product.id
        acc[id] = acc[id] || { ...product.product, amount: 0 }
        acc[id].amount++
        return acc
      }, {})
    },
    lastShippedDate() {
      return dayjs(this.selection.lastshippeddate).format('D. MMM YYYY')
    },
    nextShippedDate() {
      return dayjs(this.selection.nextshippeddate).format('D. MMM YYYY')
    },
    lockedByDate() {
      return dayjs(this.selection.lockdate).format('D. MMM YYYY')
    },
    processingDate() {
      return dayjs(this.selection.processingdate).format('D. MMM YYYY')
    },
    roleAdjust() {
      if (Object.prototype.hasOwnProperty.call(this, 'discounttype') && this.discounttype.toLowerCase().includes('role')) {
        const adj = roleAdjustPrice({ type: 'somnambulist' }, null, this.selection)
        if (Object.keys(adj).length > 0) return adj
        else return null
      }
      else return null
    },
    selectionPrice() {
      if (this.selection.productsettings.price) {
        let adjustment = 0
        if (this.roleAdjust !== null) {
          adjustment = this.roleAdjust.price
        }
        return (parseFloat(this.selection.productsettings.price) + adjustment).toFixed(2)
      }
      else {
        return false
      }
    },
    selectionRRP() {
      if (this.selection.rrp) {
        return parseFloat(this.selection.rrp).toFixed(2)
      }
      else {
        return false
      }
    },
    discount() {
      if (this.roleAdjust !== null) {
        // Modified discount rules for if the type of discount is set to Role Adjust
        if (this.customClub) {
          const regex = /[-]{0,1}[\d]*[.]{0,1}[\d]+/g
          if (this.roleAdjust.display) {
            return parseFloat(this.roleAdjust.display.match(regex)[0]).toFixed(2)
          }
          else return '0.00'
        }
        else {
          let base = 0
          if (this.selectionPrice > 0 && this.selectionRRP > 0) {
            base = parseFloat(this.selectionRRP - this.selectionPrice)
          }
          return parseFloat(base + Math.abs(this.roleAdjust.amount || 0)).toFixed(2)
        }
      }
      else if (this.customClub) {
        return parseFloat(this.selection.discountamt).toFixed(2)
      }
      else if (this.selectionPrice > 0 && this.selectionRRP > 0) {
        return parseFloat(this.selectionRRP - this.selectionPrice).toFixed(2)
      }
      else {
        return false
      }
    },
    discounttype() {
      return this.selection.discounttype
    },
    discountIsPercent() {
      if (this.customClub) {
        if (this.roleAdjust !== null) {
          if (this.roleAdjust.display) {
            return this.roleAdjust.display.includes('%')
          }
          else return false
        }
        else return true
      }
      else {
        return false
      }
    },
    frequency() {
      return (
        this.$store.getters['config/getTermLabelFromEnum'](
          this.selection.frequency
        ) || ''
      )
    },
    noItems() {
      return (
        this.$store.getters['config/getTermLabelFromEnum'](
          this.selection.noitems
        ) || ''
      )
    }
  },
  methods: {
    checkDatePassed(date) {
      return dayjs(date).unix() <= dayjs().unix()
    },
    toggleMinified() {
      this.minified = !this.minified
    },
    toggleMore(value) {
      this.showMore[value] = !this.showMore[value]
    }
  }
}
</script>

<style scoped lang="scss">
.selection {
  margin-bottom: 1em;
  background-color: var(--main-section-background-color);
  header {
    display: flex;
    .selection-details {
      width: 100%;
      .selection-number {
        background-color: var(--main-badge-background-color);
        color: var(--main-badge-text-color);
      }
      h2 {
        cursor: pointer;
        padding: 1em 0;
        width: 100%;
        span {
          display: inline-block;
          font-size: 1.2em;
          padding: 0.25em 0.5em;
          margin-right: 1em;
        }
      }
      .flex-container {
        display: flex;
        flex-wrap: wrap;
        .description-container {
          flex: 4;
          padding: 0 5em;
          font-size: 1.1em;
          .more-text {
            margin-top: 1em;
          }
          .cta-container {
            margin: 5em 0 0 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            flex-wrap: wrap;
            .selection-price {
              flex: 1;
              margin-bottom: 1em;
              .currency {
                font-size: 0.9em;
              }
              .amount {
                font-size: 2.2em;
              }
              .frequency {
                font-size: 0.9em;
              }
              .price-rrp {
                font-size: 0.5em;
                &.strike-through {
                  .amount {
                    position: relative;
                    display: inline-block;

                    &:after {
                      position: absolute;
                      transform: rotate(-6deg);
                      border-bottom: 2px solid var(--main-danger-color);
                      width: 100%;
                      left: 0;
                      top: 0.5em;
                      content: "";
                    }
                  }
                }
              }
            }
            .join-club {
              flex: 1;
            }
          }
        }
        .side-container {
          flex: 2;
          background: var(--main-wine-club-list-selection-aside-background-color);
          color: var(--main-wine-club-list-selection-aside-text-color);
          padding: 1em 2em;
          .discount {
            border: 2px solid var(--main-wine-club-list-selection-aside-highlight-color);
            color: var(--main-wine-club-list-selection-aside-highlight-color);
            padding: 0.5em;
            text-align: center;
            margin: 1em 0;
            .price {
              font-size: 0.5em;
            }
            .amount {
              font-size: 2.5em;
            }
          }
          .shipping-schedule-headline {
            margin: 1em 0;
            h4 {
              font-size: 1.1em;
            }
          }
          .selection-dates {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            margin: 0;
            padding: 1em 0;
            .date-container {
              display: flex;
              margin-bottom: 0.2em;
              .label {
                flex: 1;
                font-weight: bold;
                min-width: 10em;
              }
              .date {
                flex: 1;
                margin: 0 1em;
              }
            }
          }
          .selection-dates-timeline {
            ul {
              border-left: 1px solid var(--main-wine-club-list-selection-aside-highlight-color);
              margin-left: 0.5em;
              padding: 0;
              li {
                list-style: none;
                position: relative;
                margin-bottom: 0.5em;
                padding-left: 2em;
                &.passed {
                  &:after {
                    background: var(--main-wine-club-list-selection-aside-highlight-color);
                    border: 1px solid var(--main-wine-club-list-selection-aside-highlight-color);
                  }
                  .label {
                    color: var(--main-wine-club-list-selection-aside-date-text-color);
                    font-weight: normal;
                  }
                  .date {
                    color: var(--main-wine-club-list-selection-aside-date-text-color);
                  }
                }
                &:after {
                  content: "";
                  background: var(--main-wine-club-list-selection-aside-highlight-color);
                  border-radius: 50%;
                  width: 1em;
                  height: 1em;
                  position: absolute;
                  left: -0.5em;
                  top: 1em;
                }
                .date {
                  font-size: 0.8em;
                  display: block;
                }
                .label {
                  font-weight: bold;
                }
              }
            }
          }
        }
      }
    }
  }
}
.products-headline {
  text-align: center;
}
.products-wrapper {
  padding: 1em;
  .products-container {
    display: flex;
    justify-content: space-between;
    margin: 0;
    flex-wrap: wrap;
    .product {
      background: var(--main-card-background-color);
      color: var(--main-card-text-color);
      box-shadow: var(--main-card-drop-shadow);
      border-radius: var(--main-card-border-radius);
    }
  }
}
.products-container {
  padding-bottom: 2em;
}
</style>
